<template>
    <md-toolbar class="main-toolbar ql-project-header">
        <div class="ql-project-header-column ql-project-header-column-left">
            <md-button class="md-icon-button" @click="toggleLeftSidenav">
                <md-icon>menu</md-icon>
            </md-button>
            <h2 class="md-title ql-project-header-title">
                <span>{{projectTitle}}</span>
                <span class="project-version" v-if="currentVersion">{{currentVersion.name}}</span>
                <span class="project-version-live" v-else>Live Edit</span>
                <md-icon v-if="locked">lock</md-icon>
                <md-tooltip v-if="locked">This version is locked</md-tooltip>
            </h2>
            
            <span class="dark-mode-toggle">
                <button type="button" class="ql-theme-toggle" @click="toggleShowMode" :aria-label="localShowMode ? 'Switch to light mode' : 'Switch to dark mode'">
                    <span class="ql-theme-toggle-ico">{{ localShowMode ? '☀' : '☾' }}</span>
                    <span>{{ localShowMode ? 'Light' : 'Dark' }}</span>
                </button>
            </span>
            <project-pagination v-if="selectedThumbnailView === 'false'" v-show="!listView"
                :currentPage="currentPage" 
                :selectedPageRenameIndices="selectedPageRenameIndices"
                @pageUp="pageUp"  
                @pageDown="pageDown" 
                @setPage="setPage"
            />
            <!-- The thumbnail view's own page navigation. Same control, same
                 place in the menu bar as PDF view's, because anywhere else is
                 somewhere users do not look (Josh, 14 Sept: an earlier attempt
                 floated it above the grid and was "hard to see on the actual
                 screen").
                 ⚠ A SEPARATE instance rather than widening the one above: it
                 binds thumbnailPage, not currentPage, and emits its own event.
                 currentPage is Page Mirror's broadcast channel -- routing
                 thumbnail navigation through it would move every follower's
                 view. The two must not be conflated. -->
            <project-pagination v-if="selectedThumbnailView === 'true'" v-show="!listView"
                :currentPage="thumbnailPage"
                :step="thumbnailStep"
                :selectedPageRenameIndices="selectedPageRenameIndices"
                @setPage="setThumbnailPage"
            />
        </div>
        <div class="ql-project-header-column ql-project-header-column-right" v-if="selectedThumbnailView === 'false'" v-show="!listView">
            <md-button
                class="btn-toggle-worknote"
                :class="{ 'btn-toggle-worknote--noteslist': notesListLinked }"
                @click="handleNotesAction">
                <img :src="notesButtonIcon" :alt="tooltipText" />
                <md-tooltip>{{ tooltipText }}</md-tooltip>
            </md-button>
            <project-search />
        </div>
        <div class="ql-project-header-column ql-project-header-column-last">
            <div :style="{visibility: !connected ? 'visible' : 'hidden', display: !connected ? 'block' : 'none'}" class="connect-status">Offline</div>
            
            <span class="dark-mode-toggle" v-if="selectedPageType === 'no' && !listView && selectedThumbnailView === 'false'">
                <md-switch v-if="mView" v-model="localShowMargin" class="margin-view" id="showMargin" name="showMargin" @change="toggleMarginView">Margin View</md-switch>
                <md-switch v-else v-model="localShowMargin" class="margin-view" name="showMargin" @change="toggleMarginView">Margin View</md-switch>
            </span>
            <div class="main-header-logo-wrapper" @click="toggleListView" v-if="selectedThumbnailView === 'false'">
                <template v-if="listView">
                    <div>
                        <img :src="require('@/assets/img/Script_Icon.png')" class="main-header-view-type" alt="ListView" />
                        <img :src="require('../../assets/img/cuelist-list.svg')" alt="cuelisttext" class="main-header-logo" />
                        <md-tooltip>Switch to Script View</md-tooltip>
                    </div>
                </template>
                <template v-else>
                    <div>
                        <img :src="require('@/assets/img/List_Icon.png')" class="main-header-view-type" alt="ScriptView" />
                        <img :src="require('../../assets/img/cuelist-cue.svg')" alt="cuelisttext" class="main-header-logo" />
                        <md-tooltip>Switch to List View</md-tooltip>
                    </div>
                </template>
            </div>
            <div class="main-header-logo-wrapper" style="cursor:auto" v-else>
                <template>
                    <img :src="require('../../assets/img/cuelist-full.svg')" alt="cuelisttext" class="main-header-logo" />
                </template>
            </div>
        </div>

    </md-toolbar>
</template>

<script>
import firebase from 'firebase/app';
import { mapGetters } from 'vuex';

import NavBus from '@/buses/nav';
import ProjectPagination from '@/components/project/pagination';
import ProjectSearch from '@/components/project/search';
import { NOTESLIST_INTEGRATION_ENABLED } from '@/lib/URLs';
import { openProductionUrl } from '@/lib/lxnotes-links';

const NOTESLIST_LOGO = require('../../assets/img/thenoteslist.png');
const WORK_NOTES_ICON = require('../../assets/img/icon white.png');

const ProjectHeader = {
    name: 'projectHeader',
    props: [
        'user',
        'currentPage',
        'showMode',
        'showMarginView',
        'locked',
        'isOwner',
        'bookmarkNavOpened',
        'listView',
        'selectedPageRenameIndices',
        'thumbnailPage',
        'thumbnailStep'
    ],
    data: function() {
        return {
            localShowMargin: this.$props.showMarginView,
            connected: true
        };
    },
    computed: {
        ...mapGetters([
            'currentVersion',
            'selectedThumbnailView',
            'selectedPageType',
            'projectTitle',
            'project'
        ]),
        notesListLinked() {
            // !! so this is a Boolean, not the production id string. It binds
            // straight to Boolean props (:disabled), which warn on a String.
            return !!(NOTESLIST_INTEGRATION_ENABLED
                && this.project
                && this.project.noteslist_link_status === 'linked'
                && this.project.noteslist_production_id);
        },
        notesButtonIcon() {
            if (this.notesListLinked) {
                return NOTESLIST_LOGO;
            }
            return WORK_NOTES_ICON;
        },
        tooltipText() {
            if (this.notesListLinked) {
                return 'The Notes List';
            }
            return 'Work Notes';
        },
        mView() {
            return this.localShowMargin;
        },
        localShowMode() {
            return this.showMode;
        }
    },
    watch: {
        showMarginView(newValue) {
            this.localShowMargin = newValue;
        }
    },
    methods: {
        setThumbnailPage(page) {
            this.$emit('setThumbnailPage', page);
        },
        toggleLeftSidenav() {
            NavBus.$emit('main-nav.open');
        },
        pageDown() {
            this.$emit('pageDown', this.currentPage);
        },
        pageUp() {
            this.$emit('pageUp', this.currentPage);
        },
        setPage(number) {
            this.$emit('setPage', number);
        },
        toggleShowMode() {
            this.$emit('toggleShowMode');
        },
        toggleMarginView() {
            this.$emit('toggleMarginView');
        },
        toggleBookmarkNav() {
            this.$emit('toggleBookmarkNav');
        },
        toggleWorkNoteNav() {
            NavBus.$emit('worknote-nav.toggle');
        },
        handleNotesAction() {
            if (this.notesListLinked) {
                openProductionUrl(this.project.noteslist_production_id);
                return;
            }
            this.toggleWorkNoteNav();
        },
        expandAll() {
        },
        contractAll() {
        },
        toggleListView() {
            this.$emit('toggleListView');
            if (this.bookmarkNavOpened) {
                this.toggleBookmarkNav();
            }
        },
        handleKeyDown(e) {
            if (e.ctrlKey && e.altKey && e.code === 'KeyW') {
                // Same as the header button: The Notes List on a linked show, Work Notes otherwise.
                this.handleNotesAction();
                e.preventDefault();
            }
            if (e.ctrlKey && e.altKey && e.code === 'KeyD') {
                this.localShowMode = !this.localShowMode;
                this.toggleShowMode();
                e.preventDefault();
            }
            if (e.ctrlKey && e.altKey && e.code === 'KeyM') {
                this.localShowMargin = !this.localShowMargin;
                this.toggleMarginView();
                e.preventDefault();
            }
            if (e.ctrlKey && e.altKey && e.code === 'KeyB') {
                this.toggleBookmarkNav();
                e.preventDefault();
            }
        }
    },
    mounted: function () {
        const connectedRef = firebase.database().ref('.info/connected');
        connectedRef.on('value', (snap) => {
            if (snap.val() === true) {
                this.connected = true;
            } else {
                this.connected = false;
            }
        });
        document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy: function() {
        document.removeEventListener('keydown', this.handleKeyDown);
    },
    components: {
        'project-pagination': ProjectPagination,
        'project-search': ProjectSearch
    }
};

export default ProjectHeader;
</script>

<style lang="scss">

/* START RESPONSIVE CODE */
    $desktop: 1650px;
    $laptop: 1366px;
    $tablet: 1024px;
    $mobile: 767px;
/* END RESPONSIVE CODE */
.project-version, .project-version-live {
    font-size: 17px;
    font-weight: bold;
    margin-left: 0.5rem;

    @media screen and (max-width: $tablet) {
        font-size: 14px;
    }
}

.project-version-live {
    color: rgba(255, 255, 255, .54);
}

.project-version {
    color: #e91e63;
}

.ql-project-header {
    flex: 1;
    flex-wrap: wrap;
    justify-content: space-between;
    .margin-view {
        margin: 16px 0px 16px 0px;
        flex-direction: row-reverse;
        label.md-switch-label{
            padding-right: 8px;
            padding-left: 0px;

            @media screen and (max-width: $tablet) {
                font-size: 12px;
            }
        }
    }
    .md-switch {
        &.md-checked {
            .md-switch-container {
                background-color: darken(#5ecfdf, 30%) !important;

                .md-switch-thumb {
                    background-color: #5ecfdf;
                }
            }
        }
        label {
            font-weight: 100;
        }
    }

    .ql-project-header-title {
        margin-right: 1rem;

        &.md-title{
            @media screen and (max-width: $desktop) {
                font-size: 14px;
            }
        }

        @media screen and (max-width: $tablet) {
           margin-right: 10px;
        }

    }

    .main-header-logo-wrapper {
        position: relative;
        height: 42px;
        flex: 0 0 115px;
        cursor: pointer;
        display: flex;
        align-items: center;
        .main-header-view-type {
            position: absolute;
            left: 7px;
            top: 50%;
            transform: translateY(-50%);
            width: 22px;
            height: auto;
        }
    }
    .ql-project-header-column {
        display: flex;
        align-items: center;
        flex: 0 0 auto;

        &.ql-project-header-column-left {
            @media screen and (max-width: $tablet) {
                width: calc(100% - 240px);
                order: 1;
            }
            
            & .dark-mode-toggle {
                @media screen and (max-width: $tablet) {
                    margin-right: 0;
                }
            }
        }

        &.ql-project-header-column-right {
            flex: 1;
            max-width: 500px;
            margin-inline: auto;

            @media screen and (max-width: $tablet) {
                max-width: 100%;
                order: 3;
                padding-bottom: 8px;
            }
        }
        
        &.ql-project-header-column-last {
            flex: 1;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            width: 100%;
            max-width: 328px;
            
            @media screen and (max-width: $tablet) {
                max-width: 285px;
                order: 2;
            }
        }
    }
}

.dark-mode-toggle {
    margin-right: 10px;
    .md-ink-ripple {
        color: #5ecfdf !important;
    }
    .md-switch .md-switch-container {
        background-color: rgba(255, 255, 255, 0.38);
    }
}

.ql-theme-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.08);
    color: #eaf3f7;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
}
.ql-theme-toggle:hover { background: rgba(255, 255, 255, 0.16); }
.ql-theme-toggle:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(115, 213, 227, 0.55); }
.ql-theme-toggle-ico { font-size: 14px; line-height: 1; }

.btn-toggle-worknote {
    width: 24px;
    min-width: 0;
    padding: 0;

    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
        display: block;
    }

    &.btn-toggle-worknote--noteslist {
        width: 28px;

        img {
            width: 28px;
            height: 28px;
            border-radius: 4px;
        }
    }

    @media screen and (max-width: $tablet) {
        margin-top: 0;
        margin-bottom: 0;
    }
}

.ql-project-role {
    font-weight: 100;
    font-size: 14px;
    margin-right: 10px;
}

.bookmarkNavIcon {
    color: #5ecfdf;
}

.connect-status {
    margin: 0 1rem;
    flex: 1;

    @media screen and (max-width: $tablet) {
        font-size: 12px;
        margin-inline: 0 10px;
    }
}
</style>


// WEBPACK FOOTER //
// header.vue?512d8e9b